import { useEffect } from "react"
import { useNavigate, useParams } from "react-router"
import { useAppSelector } from "../../hooks/reduxToolkit"
import { resolveTodoExplainer } from "../../reduxToolkit/selectors/resolveTodoExplainer"
import { RedirectPlaceholder } from "../redirectPlaceholder"
import { buildTodoExplainer } from "./todoExplainerContent"

/**
 * Full-page interstitial shown between tapping a todo and reaching its
 * destination. Reads recordType/recordId/todoType from the route, resolves the
 * instructional copy and onward destination(s) from Redux, and renders the
 * registered explainer component. If there is no registered explainer for the
 * todo (or the record is gone), it redirects straight to the destination so
 * the user is never stranded.
 */
export function TodoExplainerPage() {
  const { recordType, recordId, todoType } = useParams<{
    recordType: string
    recordId: string
    todoType: string
  }>()
  const navigate = useNavigate()

  const resolved = useAppSelector((s) =>
    recordType && recordId && todoType
      ? resolveTodoExplainer(s, recordType, recordId, todoType)
      : undefined,
  )

  const Explainer = recordType && todoType && resolved
    ? buildTodoExplainer(recordType, todoType, resolved.vars)
    : undefined

  const proceedTo = resolved?.proceedTo
  const secondaryTo = resolved?.secondaryTo

  useEffect(() => {
    if (!Explainer) {
      navigate(proceedTo ?? "/todos", { replace: true })
    }
  }, [Explainer, proceedTo, navigate])

  if (!Explainer || !proceedTo) {
    // Avoids a blank frame in the in-between render, before the effect above
    // navigates away.
    return <RedirectPlaceholder />
  }

  return (
    <Explainer
      onProceed={() => navigate(proceedTo)}
      onSecondary={secondaryTo ? () => navigate(secondaryTo) : undefined}
      onCancel={() => navigate("/todos")}
    />
  )
}
